import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Store, Trash2, Minus, Plus, BadgeCheck, ArrowRight, BadgePercent } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import RecoRail from "@/components/RecoRail";
import { toast } from "sonner";

export default function CartPage() {
  const nav = useNavigate();
  const [cart, setCart] = useState(null);

  const load = useCallback(() => {
    api.get("/marketplace/cart").then((r) => setCart(r.data)).catch(() => setCart({ items: [], groups: [], subtotal: 0, unavailable: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setQty = async (it, qty) => {
    try { const r = await api.patch("/marketplace/cart/item", { product_id: it.product_id, variant_vid: it.variant_vid ?? null, qty: Math.max(0, qty) }); setCart(r.data); window.dispatchEvent(new Event("cart-updated")); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (it) => {
    try { const r = await api.delete(`/marketplace/cart/item/${it.product_id}${it.variant_vid ? `?variant_vid=${encodeURIComponent(it.variant_vid)}` : ""}`); setCart(r.data); window.dispatchEvent(new Event("cart-updated")); toast.success("Removed"); }
    catch { toast.error("Could not remove item"); }
  };

  if (!cart) return null;
  const empty = (cart.items || []).length === 0;

  return (
    <div data-testid="cart-page">
      <PageHeader title="Your Cart" icon={ShoppingCart} subtitle="Prices, stock and delivery are re-checked live — no fake availability." />

      {(cart.unavailable || []).length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="cart-unavailable">
          {cart.unavailable.map((u) => <p key={u.product_id}>• {u.name || "An item"}: {u.message}</p>)}
        </div>
      )}

      {empty ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="cart-empty">
          <ShoppingCart className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">Your cart is empty</p>
          <button onClick={() => nav("/app/shop")} className="mt-4 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Browse products</button>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {cart.groups.map((g) => (
              <div key={g.seller_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`cart-seller-${g.seller_id}`}>
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Store className="h-4 w-4 text-blue-500" /> {g.seller?.name || "Seller"}
                  {g.seller?.verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" />}
                </div>
                {g.items.map((it) => (
                  <div key={`${it.product_id}:${it.variant_vid || ""}`} className="flex items-center gap-3 border-t border-slate-100 py-3 first:border-t-0" data-testid={`cart-item-${it.product_id}`}>
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-50">
                      {it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Store className="h-6 w-6 text-slate-300" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-sm font-medium text-slate-800">{it.name}</p>
                      {it.variant_label && <p className="text-xs text-slate-400" data-testid={`cart-variant-${it.product_id}`}>Variant: {it.variant_label}</p>}
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-bold text-slate-900">₹{it.price}</p>
                        {it.mrp > it.price && <>
                          <span className="text-xs text-slate-400 line-through">₹{it.mrp}</span>
                          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`cart-discount-${it.product_id}`}>{it.discount_pct || Math.round((it.mrp - it.price) / it.mrp * 100)}% OFF</span>
                        </>}
                      </div>
                      {it.return_policy && (it.return_policy.non_returnable
                        ? <span className="mt-0.5 inline-block rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600" data-testid={`cart-return-${it.product_id}`}>Non-returnable</span>
                        : (it.return_policy.window_days > 0 && <span className="mt-0.5 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`cart-return-${it.product_id}`}>{it.return_policy.window_days}-day returns</span>))}
                    </div>
                    <div className="flex items-center gap-1 rounded-full border border-slate-200">
                      <button onClick={() => setQty(it, it.qty - 1)} data-testid={`qty-dec-${it.product_id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`qty-${it.product_id}`}>{it.qty}</span>
                      <button onClick={() => setQty(it, it.qty + 1)} disabled={it.qty >= it.stock} data-testid={`qty-inc-${it.product_id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button onClick={() => remove(it)} data-testid={`remove-${it.product_id}`} className="rounded-full p-2 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5" data-testid="cart-summary">
            <h3 className="font-semibold text-slate-800">Order Summary</h3>
            <div className="mt-3 flex justify-between text-sm text-slate-600"><span>Items ({cart.count})</span><span>₹{cart.subtotal}</span></div>
            {cart.total_discount > 0 && (
              <div className="mt-1 flex justify-between text-sm text-emerald-600" data-testid="cart-discount"><span>Discounts</span><span>− ₹{cart.total_discount}</span></div>
            )}
            {(cart.applied_promotions || []).length > 0 && (
              <div className="mt-2 space-y-1" data-testid="cart-applied-promos">
                {cart.applied_promotions.map((p) => (
                  <div key={p.promo_id} className="flex items-center gap-1.5 text-xs text-emerald-700"><BadgePercent className="h-3 w-3" /> {p.name}{p.code ? ` (${p.code})` : ""} {p.amount > 0 ? `− ₹${p.amount}` : ""}</div>
                ))}
              </div>
            )}
            <div className="mt-1 flex justify-between text-sm text-slate-600"><span>Delivery</span><span data-testid="cart-shipping" className={cart.shipping_total > 0 ? "" : "text-emerald-600"}>{cart.shipping_total > 0 ? `₹${cart.shipping_total}` : "Free"}</span></div>
            <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-base font-bold text-slate-900"><span>Total</span><span data-testid="cart-total">₹{cart.payable_total ?? cart.final_total ?? cart.subtotal}</span></div>
            <button onClick={() => nav("/app/checkout")} data-testid="checkout-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg py-3 text-sm font-semibold text-white transition hover:opacity-90">
              Proceed to Checkout <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {!empty && (
        <RecoRail path="/marketplace/cart/recommendations" rail="cart" title="You might also like" subtitle="Real picks that go well with your cart" layout="grid" limit={6} />
      )}
    </div>
  );
}
